import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setStatus('error');
      setMessage('Password must be at least 8 characters');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/auth/reset-password/${token}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password })
        }
      );

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage('Password reset successful!');
        
        // Store token and user data
        if (data.token) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setStatus('error');
        setMessage(data.error || 'Password reset failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('An error occurred. Please try again.');
    }
  };

  if (!token) {
    return (
      <div className="auth-page" style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Container>
          <div className="d-flex justify-content-center">
            <Card style={{ maxWidth: '450px', width: '100%' }}>
              <Card.Body className="p-5 text-center">
                <h2 className="mb-4"><span role="img" aria-label="microphone">🎙️</span> EchoBrief</h2>
                <Alert variant="danger">
                  <Alert.Heading>Invalid Link</Alert.Heading>
                  <p>No reset token provided. Please request a new password reset.</p>
                </Alert>
                <Link to="/forgot-password">
                  <Button variant="primary">Request New Link</Button>
                </Link>
              </Card.Body>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="auth-page" style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Container>
        <div className="d-flex justify-content-center">
          <Card style={{ maxWidth: '450px', width: '100%' }}>
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <h2><span role="img" aria-label="microphone">🎙️</span> EchoBrief</h2>
                <p className="text-muted">Create a new password</p>
              </div>

              {status === 'success' ? (
                <>
                  <Alert variant="success">
                    <Alert.Heading><span role="img" aria-label="checkmark">✅</span> Password Reset!</Alert.Heading>
                    <p>{message}</p>
                    <p className="mb-0">Redirecting to dashboard...</p>
                  </Alert>
                </>
              ) : (
                <>
                  {status === 'error' && (
                    <Alert variant="danger" dismissible onClose={() => setStatus('idle')}>
                      {message}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={8}
                        disabled={status === 'loading'}
                      />
                      <Form.Text className="text-muted">
                        Must be at least 8 characters
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Confirm Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={status === 'loading'}
                      />
                    </Form.Group>

                    <Button 
                      variant="primary" 
                      type="submit" 
                      className="w-100"
                      disabled={status === 'loading'}
                    >
                      {status === 'loading' ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Resetting...
                        </>
                      ) : (
                        'Reset Password'
                      )}
                    </Button>
                  </Form>

                  <div className="text-center mt-4">
                    <Link to="/login" className="text-decoration-none">
                      ← Back to Login
                    </Link>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default ResetPassword;
